<script setup lang="ts">
/**
 * PlanTicket — a subscription plan as a paper theatre ticket: the period on the stub,
 * a perforation with a notch cut from each side, the price below (with the crossed-out
 * original and the saving, when given). A stamp on the corner marks "popular" / "best
 * value". Dark stock; the chosen one turns cream, straightens, lifts and gets a tick.
 * `deal` = it's dealt onto the counter when it first appears (the paywall), staggered
 * by `index`. A real toggle button (aria-pressed); the parent handles the click.
 */
import { Check } from '@lucide/vue';

withDefaults(
  defineProps<{
    period: string;
    price: number | string;
    originalPrice?: number | string;
    /** Saving label, e.g. "save 42%". */
    save?: string;
    stamp?: { label: string; tone: 'wine' | 'moss' };
    selected: boolean;
    index?: number;
    deal?: boolean;
  }>(),
  { originalPrice: undefined, save: undefined, stamp: undefined, index: 0, deal: false },
);

const TILTS = [-1.2, 0.6, 1.3];
</script>

<template>
  <button
    type="button"
    :class="[
      'plan-ticket relative flex flex-col items-center px-1.5 pb-3 pt-5 text-center',
      selected ? 'is-on paper-tag text-paper-stock-ink' : 'paper-sheet text-foreground',
      deal && 'is-dealt',
    ]"
    :style="{ '--tilt': `${TILTS[index % TILTS.length]}deg`, '--i': index }"
    :aria-pressed="selected"
  >
    <span
      v-if="stamp"
      :class="['pt-stamp type-overline', stamp.tone === 'wine' ? 'bg-stock-wine' : 'bg-stock-moss']"
    >
      {{ stamp.label }}
    </span>
    <span class="type-label">{{ period }}</span>
    <span
      class="pt-perf"
      aria-hidden="true"
    />
    <s
      v-if="originalPrice"
      class="pt-old font-sans text-sm"
    >{{ originalPrice }}&#8239;₽</s>
    <span class="font-sans text-2xl font-bold leading-none">{{ price }}&#8239;₽</span>
    <span
      v-if="save"
      class="pt-save mt-2 font-sans text-xs font-semibold"
    >
      {{ save }}
    </span>
    <Check
      v-if="selected"
      class="pt-tick h-4 w-4"
      aria-hidden="true"
    />
  </button>
</template>

<style scoped>
.plan-ticket {
  border-radius: var(--paper-radius-sm);
  transform: rotate(var(--tilt));
  transition:
    transform 0.22s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.22s ease-out;
}
.plan-ticket.is-dealt {
  animation: pt-deal 0.42s cubic-bezier(0.22, 1, 0.36, 1) calc(0.18s + var(--i) * 0.07s) backwards;
}
.plan-ticket.is-on {
  transform: translateY(-5px);
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 0.55),
    0 0 0 2px var(--primary-ink),
    0 14px 18px -8px var(--paper-shade);
}
.plan-ticket:active {
  transform: rotate(var(--tilt)) translateY(1px);
}
.plan-ticket:focus-visible {
  outline: 2px solid var(--paper-stock);
  outline-offset: 3px;
}
@keyframes pt-deal {
  from {
    opacity: 0;
    transform: translateY(18px) rotate(calc(var(--tilt) * -3));
  }
}
/* Perforation: a dashed tear line across the ticket, with a notch cut out of each side
   (discs of the page colour — cheap, no mask). */
.pt-perf {
  position: relative;
  align-self: stretch;
  margin: 10px -6px 8px;
  /* Only the dashes are faint — the notches (::before/::after) must stay opaque. */
  border-top: 1.5px dashed color-mix(in srgb, currentColor 35%, transparent);
}
.pt-perf::before,
.pt-perf::after {
  content: '';
  position: absolute;
  top: -6.75px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--background);
}
.pt-perf::before {
  left: -7px;
}
.pt-perf::after {
  right: -7px;
}
.is-on .pt-perf {
  border-top-color: color-mix(in srgb, currentColor 45%, transparent);
}
.pt-old {
  opacity: 0.6;
}
.pt-save {
  padding: 2px 6px;
  white-space: nowrap;
  border: 1.5px dashed currentColor;
  border-radius: 999px;
  opacity: 0.85;
}
.pt-tick {
  position: absolute;
  left: 6px;
  top: 6px;
}
/* The badge is a stamp stuck on the ticket's corner, above its top edge. */
.pt-stamp {
  position: absolute;
  top: -19px;
  right: -6px;
  max-width: 86%;
  padding: 2px 6px;
  border-radius: var(--paper-radius-xs);
  color: var(--foreground);
  font-size: 10px;
  line-height: 1.2;
  letter-spacing: 0.04em;
  transform: rotate(4deg);
  box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.18), 0 3px 6px -2px var(--paper-shade);
}
/* Short phones (SE-class): tighter, so a row of tickets + the CTA fit. */
@media (max-height: 720px) {
  .plan-ticket {
    padding-top: 1rem;
    padding-bottom: 0.625rem;
  }
  .pt-perf {
    margin-top: 7px;
    margin-bottom: 5px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .plan-ticket {
    animation: none !important;
    transition: none;
  }
}
</style>
